import { type Model } from 'flexlayout-react';
import { useEffect, type RefObject } from 'react';

import { isInputElement } from 'app/modules/wallforming/components/scenes/objects/utils/sceneUtils';
import { PlacementTab } from 'app/modules/wallforming/components/setting-container/types/placementTab';
import { SettingView } from 'app/modules/wallforming/components/setting-container/types/settingView';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

const previousNavigationCodes = new Set(['ArrowUp', 'KeyW']);
const nextNavigationCodes = new Set(['ArrowDown', 'KeyS']);

interface SettingOptionsLayoutProps {
    catalogueViewRef: RefObject<HTMLDivElement | null>;
    placementViewRef: RefObject<HTMLDivElement | null>;
    prevSettingViewRef: RefObject<SettingView>;
    settingModelRef?: RefObject<Model | null>;
}

export const useSettingEventHandler = ({
    catalogueViewRef,
    placementViewRef,
    prevSettingViewRef
}: SettingOptionsLayoutProps) => {
    // Handle setting options layout visibility
    useEffect(() => {
        const unsubscribe = usePlacementStore.subscribe(
            (state) => state.selectedSettingView,
            (newSettingView) => {
                const previousViewRef =
                    prevSettingViewRef.current === SettingView.CATALOGUE ? catalogueViewRef : placementViewRef;
                const activeElement = document.activeElement;
                if (activeElement instanceof HTMLElement && previousViewRef.current?.contains(activeElement)) {
                    activeElement.blur();
                }

                prevSettingViewRef.current = newSettingView;

                if (newSettingView === SettingView.CATALOGUE) {
                    catalogueViewRef.current?.classList.remove('hidden');
                    placementViewRef.current?.classList.add('hidden');
                } else if (newSettingView === SettingView.AUTOMATED_PLACEMENT) {
                    placementViewRef.current?.classList.remove('hidden');
                    catalogueViewRef.current?.classList.add('hidden');
                }
            }
        );

        return unsubscribe;
    }, [catalogueViewRef, placementViewRef, prevSettingViewRef]);

    // Handle navigation through solutions or catalogue via arrow 'up'/'down' or W/S keys
    useEffect(() => {
        const handleOnArrowKeyDown = (event: KeyboardEvent) => {
            const { code } = event;
            const isPreviousNavigation = previousNavigationCodes.has(code);
            const isNextNavigation = nextNavigationCodes.has(code);
                
            if ((!isPreviousNavigation && !isNextNavigation) || isInputElement(event.target)) {
                return;
            }

            event.preventDefault();
            event.stopPropagation();
            if (event.target instanceof HTMLElement) {
                event.target.blur();
            }

            const state = usePlacementStore.getState();
            const delta = isPreviousNavigation ? -1 : 1;

            if (
                state.selectedSettingView === SettingView.CATALOGUE ||
                (state.selectedSettingView === SettingView.AUTOMATED_PLACEMENT &&
                    state.automatedPlacementTab === PlacementTab.CATALOGUE)
            ) {
                const catalogueRoot =
                    state.selectedSettingView === SettingView.CATALOGUE
                        ? catalogueViewRef.current
                        : placementViewRef.current?.querySelector<HTMLElement>('[data-automated-catalogue]');

                const rows = Array.from(
                    catalogueRoot?.querySelectorAll<HTMLElement>('[data-catalogue-article-id]') ?? []
                );
                if (rows.length > 0) {
                    const currentIndex = rows.findIndex(
                        (row) => row.dataset.catalogueArticleId === state.selectedArticleId
                    );
                    const nextIndex =
                        currentIndex < 0
                            ? delta < 0
                                ? rows.length - 1
                                : 0
                            : (currentIndex + delta + rows.length) % rows.length;
                    state.setSelectedArticleId(rows[nextIndex].dataset.catalogueArticleId ?? null);
                    return;
                }
            }

            if (
                state.selectedSettingView === SettingView.AUTOMATED_PLACEMENT &&
                state.automatedPlacementTab === PlacementTab.SOLUTIONS
            ) {
                const count = state.placementSolutions.length;
                if (count === 0) {
                    return;
                }

                const current = state.selectedPlacementSolution ?? (delta < 0 ? 0 : -1);
                state.setSelectedPlacementSolution((current + delta + count) % count);
            }
        };

        window.addEventListener('keydown', handleOnArrowKeyDown, true);
        return () => window.removeEventListener('keydown', handleOnArrowKeyDown, true);
    }, [catalogueViewRef, placementViewRef]);
};
